import type { SortOption } from "@/features/common/types/sort";

import { CONVERSATION_STATUS } from "@/types/conversation";

/**
 * A set filter keeps HeroUI's own trigger look; its value and the filter count
 * say it is set. The end padding leaves room for the clear button.
 */
export const REPORT_FILTER_ACTIVE_TRIGGER_CLASS = "!pe-14";

/**
 * The value a select shows when nothing is filtered. It is not sent: a select
 * holding `undefined` renders an empty trigger, which reads as broken rather
 * than as "everything".
 */
export const REPORT_FILTER_ANY = "any";

export const REPORT_LIST_FILTERS_COPY = {
  button: "Filters",
  /** The page bar's popover; a list card names its own ("Filter patients"). */
  heading: "Filters",
  reset: "Reset",
} as const;

/** Matches the backend sentinel - contacts sitting on no stage at all. */
export const LIFECYCLE_NONE = "none";

/** Matches the backend sentinel - contacts nobody owns. */
export const ASSIGNEE_NONE = "none";

/** Matches the backend sentinel - partner deliveries booked with no courier. */
export const COURIER_NONE = "none";

export const REPORT_STATUS_OPTIONS: SortOption[] = [
  { key: REPORT_FILTER_ANY, label: "All statuses" },
  { key: CONVERSATION_STATUS.OPEN, label: "Open" },
  { key: CONVERSATION_STATUS.CLOSED, label: "Closed" },
  { key: CONVERSATION_STATUS.SNOOZED, label: "Snoozed" },
];

export type ReportFilterField =
  | "lifecycle"
  | "status"
  | "assignee"
  | "campaign"
  | "channel"
  | "date"
  /** An appointment's or an order's own status - never a conversation's. */
  | "recordStatus"
  /** Booked here, or synced in from one of the workspace's integrations. */
  | "source"
  /** The person behind the record - who booked it, who replied, who owns it. */
  | "actor"
  /** Whether the record has a recorded monetary value. */
  | "priced"
  /** An appointment's department - its treatment's, or its doctor's. */
  | "department"
  /** The treatment booked - what the "By treatment" breakdown drills into. */
  | "treatment"
  /** The doctor the visit is with. */
  | "doctor"
  /** Free consultation or paid treatment. */
  | "bookingType"
  /** Still expected, or been and gone. */
  | "when"
  /** A name or phone, typed. */
  | "search"
  /** A money range. A row with no price never matches one. */
  | "amount"
  /** Has paid, never paid, or still owes. */
  | "paid"
  /** How a contact was created - the Contacts report's source. */
  | "origin"
  /** Receipts, advances or both - the Collected ledger's split. */
  | "ledgerType"
  /** What opened the conversation - an ad, a follow-up, a teammate by hand. */
  | "openedBy"
  /** Incoming or outgoing - the message log's own axis. */
  | "direction";

/** Filters that narrow by an amount, which a role without `reports.amount` can't read. */
export const AMOUNT_FILTER_FIELDS: ReportFilterField[] = [
  "priced",
  "amount",
  "paid",
];

export const REPORT_PRICED_OPTIONS = [
  { key: REPORT_FILTER_ANY, label: "All" },
  { key: "yes", label: "Value recorded" },
  { key: "no", label: "No value recorded" },
];

/** The money question about a person. Matches the backend `ContactsPaidFilter`. */
export const REPORT_PAID_OPTIONS = [
  { key: REPORT_FILTER_ANY, label: "Paid or not" },
  { key: "paid", label: "Has paid" },
  { key: "unpaid", label: "Never paid" },
  { key: "outstanding", label: "Has an outstanding balance" },
];

/** The Collected ledger mixes two kinds; the two invoice ledgers hold one. */
export const REPORT_LEDGER_TYPE_OPTIONS = [
  { key: REPORT_FILTER_ANY, label: "Receipts and advances" },
  { key: "receipt", label: "Receipts" },
  { key: "advance", label: "Advances" },
];

/**
 * The quick picks under the Amount filter, as cut points in the organisation
 * currency. Under the first, between neighbours, over the last.
 */
// ponytail: one fixed ladder; scale it per currency when a non-NPR clinic asks.
export const REPORT_AMOUNT_PRESET_BOUNDS = [1000, 5000, 20000, 50000];

/** The above-zero pick on a field that is money paid; a generic amount keeps "More than zero". */
export const REPORT_PAID_ABOVE_ZERO_LABEL = "Has a payment";
